import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  Loader2, Copy, Share2, Mail, MessageSquare, Gift, Crown,
  Infinity as InfinityIcon, CheckCircle2, Clock, Users,
} from 'lucide-react';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const APP_URL = (typeof window !== 'undefined' && window.location?.origin) || '';

const SHARE_SUBJECT = 'Join me on RanchWorks — 60-day free trial';
const SHARE_BODY = (link, code) =>
  `Hey — I'm using RanchWorks for my ranch/equine bookings and thought you'd want in.\n\n` +
  `Sign up with my referral link and you'll get 60 days free:\n${link}\n\n` +
  `(Or enter referral code ${code} on the signup page.)\n\n` +
  `— Sent from RanchWorks`;

export default function ReferralsPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/dashboard/referrals`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load referrals');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  if (!data) {
    return (
      <DashboardLayout>
        <div className="text-center text-muted-foreground py-12">
          Unable to load referral info.
        </div>
      </DashboardLayout>
    );
  }

  const {
    referralCode,
    referralCount = 0,
    isFoundingProvider,
    lifetimeAccess,
    invitedProviders = [],
    activeReferralCount = 0,
  } = data;

  const shareLink = referralCode
    ? `${APP_URL}/signup-provider?ref=${encodeURIComponent(referralCode)}`
    : '';

  const copy = async (text, label = 'Copied') => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(label);
    } catch {
      toast.error('Could not copy — please copy manually');
    }
  };

  const shareNative = async () => {
    if (!shareLink) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: SHARE_SUBJECT,
          text: SHARE_BODY(shareLink, referralCode),
          url: shareLink,
        });
      } catch {
        // user cancelled — silent
      }
    } else {
      copy(shareLink, 'Link copied — paste anywhere');
    }
  };

  const mailtoHref = referralCode
    ? `mailto:?subject=${encodeURIComponent(SHARE_SUBJECT)}&body=${encodeURIComponent(SHARE_BODY(shareLink, referralCode))}`
    : '';
  const smsHref = referralCode
    ? `sms:?body=${encodeURIComponent(SHARE_BODY(shareLink, referralCode))}`
    : '';

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6" data-testid="referrals-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Gift className="h-7 w-7 text-amber-600" />
              Refer a Provider
            </h1>
            <p className="text-muted-foreground mt-1 text-sm">
              Share your code — every provider that signs up gets a free 60-day trial.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {isFoundingProvider && (
              <Badge
                className="bg-amber-100 text-amber-800 border-amber-300"
                data-testid="founder-badge"
              >
                <Crown className="h-3 w-3 mr-1" /> Founding Provider
              </Badge>
            )}
            {lifetimeAccess && (
              <Badge
                className="bg-purple-100 text-purple-800 border-purple-300"
                data-testid="lifetime-badge"
              >
                <InfinityIcon className="h-3 w-3 mr-1" /> Lifetime Access
              </Badge>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Card data-testid="stat-invited">
            <CardContent className="p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-1">
                <Users className="h-3 w-3" /> Invited
              </p>
              <p className="text-3xl font-bold mt-1">{referralCount}</p>
            </CardContent>
          </Card>
          <Card data-testid="stat-active">
            <CardContent className="p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3" /> Active referrals
              </p>
              <p className="text-3xl font-bold mt-1">{activeReferralCount}</p>
            </CardContent>
          </Card>
          <Card className="col-span-2 md:col-span-1" data-testid="stat-trial">
            <CardContent className="p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-1">
                <Clock className="h-3 w-3" /> Their reward
              </p>
              <p className="text-lg font-semibold mt-1">60-day free trial</p>
            </CardContent>
          </Card>
        </div>

        {/* Share card */}
        <Card>
          <CardHeader>
            <CardTitle>Your referral code</CardTitle>
            <CardDescription>
              Send this to ranch/equine providers in your network. They get 60 days free; you build your bench.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Code */}
            <div>
              <label className="text-xs uppercase tracking-wide text-muted-foreground">Code</label>
              <div className="flex items-center gap-2 mt-1">
                <code
                  className="flex-1 px-3 py-2 rounded border bg-muted font-mono text-base sm:text-lg"
                  data-testid="referral-code-display"
                >
                  {referralCode || '—'}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copy(referralCode, 'Code copied')}
                  disabled={!referralCode}
                  data-testid="copy-code-btn"
                >
                  <Copy className="h-4 w-4 mr-1" /> Copy
                </Button>
              </div>
            </div>

            {/* Link */}
            <div>
              <label className="text-xs uppercase tracking-wide text-muted-foreground">
                Sign-up link
              </label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  readOnly
                  value={shareLink}
                  className="font-mono text-xs sm:text-sm"
                  data-testid="referral-link-input"
                  onFocus={(e) => e.target.select()}
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copy(shareLink, 'Link copied')}
                  disabled={!shareLink}
                  data-testid="copy-link-btn"
                >
                  <Copy className="h-4 w-4 mr-1" /> Copy
                </Button>
              </div>
            </div>

            {/* Share actions */}
            <div className="flex flex-wrap gap-2 pt-2">
              <Button
                onClick={shareNative}
                disabled={!shareLink}
                data-testid="share-btn"
              >
                <Share2 className="h-4 w-4 mr-2" /> Share
              </Button>
              <Button
                asChild
                variant="outline"
                disabled={!referralCode}
                data-testid="share-email-btn"
              >
                <a href={mailtoHref} data-testid="email-btn">
                  <Mail className="h-4 w-4 mr-2" /> Email
                </a>
              </Button>
              <Button
                asChild
                variant="outline"
                disabled={!referralCode}
                data-testid="share-sms-btn"
              >
                <a href={smsHref} data-testid="text-btn">
                  <MessageSquare className="h-4 w-4 mr-2" /> Text
                </a>
              </Button>
            </div>

            {!isFoundingProvider && (
              <p className="text-xs text-muted-foreground pt-2 border-t">
                Want even more perks?{' '}
                <span className="font-medium">Founding Provider</span> status is granted by RanchWorks admins — reach out to your account contact to apply.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Invited list */}
        <Card>
          <CardHeader>
            <CardTitle>Providers you've invited</CardTitle>
            <CardDescription>
              {invitedProviders.length === 0
                ? "Nobody yet — share your code above to get started."
                : `${invitedProviders.length} provider${invitedProviders.length === 1 ? '' : 's'} have used your code.`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {invitedProviders.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground text-sm" data-testid="no-referrals">
                <Gift className="h-10 w-10 mx-auto mb-2 opacity-40" />
                Your referrals will appear here.
              </div>
            ) : (
              <div className="space-y-2" data-testid="invited-list">
                {invitedProviders.map((p) => {
                  const isActive = p.accountStatus === 'ACTIVE' && p.promoStatus === 'ACTIVE';
                  const isExpired = p.promoEndDate && new Date(p.promoEndDate) < new Date();
                  return (
                    <div
                      key={p.id}
                      className="flex items-start justify-between gap-3 p-3 rounded border bg-card"
                      data-testid={`invited-row-${p.id}`}
                    >
                      <div className="min-w-0">
                        <p className="font-medium truncate">
                          {p.businessName || p.ownerName || p.email || 'Unnamed provider'}
                        </p>
                        {p.email && (
                          <p className="text-xs text-muted-foreground truncate">{p.email}</p>
                        )}
                        {p.createdAt && (
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Joined {format(new Date(p.createdAt), 'MMM d, yyyy')}
                            {p.promoEndDate && !isExpired && (
                              <> · Trial ends {format(new Date(p.promoEndDate), 'MMM d, yyyy')}</>
                            )}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        {isActive ? (
                          <Badge className="bg-green-100 text-green-800 border-green-300 text-[10px]">
                            Active
                          </Badge>
                        ) : p.accountStatus === 'PENDING' ? (
                          <Badge variant="outline" className="text-[10px]">Pending</Badge>
                        ) : (
                          <Badge variant="secondary" className="text-[10px]">
                            {p.accountStatus || 'Inactive'}
                          </Badge>
                        )}
                        {isExpired && (
                          <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-300">
                            Trial ended
                          </Badge>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
